import React, { useState, useEffect } from 'react';
import { Button, Card, Space, Tooltip, Typography, Spin, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FileErrorState } from './FileViewer';

interface XMLViewerProps {
  fileUrl: string;
}

export const XMLViewer: React.FC<XMLViewerProps> = ({ fileUrl }) => {
  const [xmlContent, setXmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [showFormatted, setShowFormatted] = useState<boolean>(true);

  useEffect(() => {
    const loadXMLContent = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const text = await response.text();

        // Basic XML validation
        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(text, 'text/xml');
          const parseError = xmlDoc.getElementsByTagName('parsererror');

          if (parseError.length > 0) {
            throw new Error('Invalid XML format');
          }
        } catch (parseError) {
          console.warn('XML parsing warning:', parseError);
          // Continue anyway - might be partially valid XML
        }

        setXmlContent(text);
      } catch (err) {
        console.error('Error loading XML:', err);
        setError(err instanceof Error ? err.message : 'Failed to load XML file');
      } finally {
        setLoading(false);
      }
    };

    if (fileUrl) {
      loadXMLContent();
    }
  }, [fileUrl]);

  const handleDownload = () => {
    try {
      const element = document.createElement('a');
      const file = new Blob([xmlContent], { type: 'application/xml' });
      element.href = URL.createObjectURL(file);
      element.download = fileUrl.split('/').pop() || 'file.xml';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      URL.revokeObjectURL(element.href);
      message.success('File downloaded successfully');
    } catch (err) {
      console.error('Download error:', err);
      message.error('Failed to download file');
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(xmlContent);
      message.success('XML content copied to clipboard');
    } catch (err) {
      console.error('Copy error:', err);
      message.error('Failed to copy content');
    }
  };

  const formatXML = (xml: string): string => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'text/xml');
      const serializer = new XMLSerializer();
      const formatted = serializer.serializeToString(xmlDoc);

      // Add proper indentation
      let result = '';
      let indent = 0;
      const lines = formatted.split(/>\s*</);

      lines.forEach((line, index) => {
        if (index > 0) {
          if (line.match(/^\/\w/)) {
            indent--;
          }
          result += '\n' + '  '.repeat(indent);
        }

        if (index < lines.length - 1) {
          result += '<' + line + '>';
        } else {
          result += '<' + line;
        }

        if (line.match(/^\w[^>]*[^\/]$/)) {
          indent++;
        }
      });

      return result;
    } catch (err) {
      console.warn('XML formatting failed, showing raw content:', err);
      return xml;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Spin size="large" />
        <Typography.Text style={{ marginTop: '1rem', display: 'block' }}>
          Loading XML file...
        </Typography.Text>
      </div>
    );
  }

  if (error) {
    return (
      <FileErrorState
        error={error}
        fileName={fileUrl.split('/').pop()}
      />
    );
  }

  const displayContent = showFormatted ? formatXML(xmlContent) : xmlContent;
  const syntaxStyle = theme === 'dark' ? oneDark : oneLight;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Card
        size="small"
        style={{
          borderRadius: 0,
          borderLeft: 'none',
          borderRight: 'none',
          borderTop: 'none'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Space>
            <Typography.Text strong>
              {fileUrl.split('/').pop() || 'XML File'}
            </Typography.Text>
            <Typography.Text type="secondary">
              ({(xmlContent.length / 1024).toFixed(1)} KB)
            </Typography.Text>
          </Space>

          <Space>
            <Tooltip title={showFormatted ? "Show raw XML" : "Show formatted XML"}>
              <Button
                size="small"
                onClick={() => setShowFormatted(!showFormatted)}
                type={showFormatted ? "primary" : "default"}
                icon={<FontAwesomeIcon icon="fa-solid fa-gear" />}
              >
                Format
              </Button>
            </Tooltip>

            <Tooltip title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}>
              <Button
                size="small"
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                icon={<FontAwesomeIcon icon="fa-solid fa-palette" />}
              />
            </Tooltip>

            <Tooltip title="Copy to clipboard">
              <Button
                size="small"
                onClick={handleCopyToClipboard}
                icon={<FontAwesomeIcon icon="fa-solid fa-copy" />}
              />
            </Tooltip>

            <Tooltip title="Download file">
              <Button
                size="small"
                onClick={handleDownload}
                icon={<FontAwesomeIcon icon="fa-solid fa-download" />}
              />
            </Tooltip>
          </Space>
        </div>
      </Card>

      {/* Content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        backgroundColor: theme === 'dark' ? '#1e1e1e' : '#ffffff'
      }}>
        <SyntaxHighlighter
          language="xml"
          style={syntaxStyle}
          showLineNumbers={true}
          wrapLines={true}
          customStyle={{
            margin: 0,
            borderRadius: 0,
            minHeight: '100%',
            fontSize: '13px',
            lineHeight: '1.4',
          }}
        >
          {displayContent}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};
